import React, {useEffect, useState} from 'react';
import {amountFormatter, showError} from '@utils/helpers';
import moment from "moment";
import TableOrderService from "@services/TableOrderService";
import {useParams} from "react-router";
import {useNavigate} from "react-router-dom";
import GreenBadge from "@core/badges/GreenBadge";
import RedBadge from "@core/badges/RedBadge";
import OrangeBadge from "@core/badges/OrangeBadge";
import BlueBadge from "@core/badges/BlueBadge";
import DefaultBadge from "@core/badges/DefaultBadge";
import logo from "@assets/svg/logo.svg";
import {useTranslation} from "react-i18next";

const DetailRow = ({title, value}) => (
  <div className="flex justify-between my-2">
    <span className="text-sm leading-none font-normal text-gray-600 whitespace-nowrap">{title}</span>
    <span className="text-sm ml-6 leading-5 font-medium text-end">{value}</span>
  </div>
);

const ProductRow = ({title, price, qty, totalPrice}) => (
  <div className="grid grid-cols-5 my-2">
    <span className="col-span-2 text-sm leading-5 font-normal text-gray-600 whitespace-nowrap truncate">{title}</span>
    <span className="text-sm leading-5 font-normal text-end">{price}</span>
    <span className="text-sm leading-5 font-normal text-end">x{qty}</span>
    <span className="text-sm leading-5 font-medium text-end">{totalPrice}</span>
  </div>
);

const orderStatusFormatter = (row, t) => {
  switch (row?.orderStatus) {
    case 0:
      return (
        <h4>
          <OrangeBadge text={t('requested')}/>
        </h4>
      );
    case 1:
      return (
        <h4>
          <GreenBadge text={t('accepted')}/>
        </h4>
      );
    case 3:
      return (
        <h4>
          <DefaultBadge text={t('preparing')} bgColor="text-yellow-800" textColor="bg-yellow-100"/>
        </h4>
      );
    case 4:
      return (
        <h4>
          <OrangeBadge text={t('paymentInProgress')} color={'info'}/>
        </h4>
      );
    case 5:
      return (
        <h4>
          <RedBadge text={t('cancelled')}/>
        </h4>
      );
    case 6:
      return (
        <h4>
          <GreenBadge text={t('delivering')} />
        </h4>
      );
    case 7:
      return (
        <h4>
          <BlueBadge text={t('delivered')} color={'primary'}/>
        </h4>
      );
    default:
      return (
        <h4>
          <DefaultBadge text="Unknown"/>
        </h4>
      );
  }
};

const SuccessfulOrder = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {orderId} = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const phoneNumber = urlParams.get('phoneNumber');
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    if (orderId) {
      TableOrderService.getOrder(orderId, phoneNumber)
        .then((response) => {
          setInvoice(response?.data)
        })
        .catch((err) => {
          showError(err.response.data.message);
        })
    }
  }, [orderId, phoneNumber]);

  return (
    <div className='mt-3 px-5'>
      <div className="text-center mb-10 flex-col flex justify-center">
        <img alt="logo" className="h-10" src={logo}/>
        <h2 className="mt-3 text-3xl font-bold text-gray-900">{t('thankYouForYourOrder')}</h2>
      </div>
      <div className="flex justify-between items-center my-5">
        <div className="flex flex-col">
          <span className="text-sm leading-none font-medium pb-1">{t('invoiceDate')}</span>
          <span className="text-sm leading-5 text-gray-600">{moment(invoice?.orderDate).format("DD-MM-YYYY")}</span>
        </div>
        {orderStatusFormatter(invoice, t)}
      </div>

      <div className="bg-gray-100 rounded-lg px-3 py-2">
        <DetailRow title={t('orderId')} value={invoice?.orderCounter}/>
        <DetailRow title={t('restaurant')} value={invoice?.pageName}/>
        <DetailRow title={t('table')} value={invoice?.table?.name}/>
        {invoice?.clientPhone && <DetailRow title={t('phoneNumber')} value={invoice?.clientPhone}/>}
        {invoice?.description && <DetailRow title={t('orderDetails')} value={invoice?.description}/>}
        <DetailRow title={t('paymentMethod')} value={invoice?.paymentMethodDescription}/>
      </div>
      <div className="border-b px-3 pt-2">
        {invoice && invoice?.orderProducts.map((product) => {
          return <ProductRow title={product?.name} price={product?.price} qty={product?.qty}
                             totalPrice={product?.qty * product?.price}/>
        })}
      </div>
      <div className="px-3 pt-2">
        <DetailRow
          title={t('total')}
          value={<span className="text-emerald-500">{amountFormatter(invoice?.totalValue, 'ALL')}</span>}
        />
      </div>
      <div className="sticky w-full bottom-0 flex items-center justify-end py-3 px-3">
        <button onClick={() => {
          navigate(`/qr/${invoice?.table?.tableCode}`)
          window.scrollTo(0, 0);
        }}
                className="py-4 px-2 text-base group w-full relative justify-center font-medium rounded-3xl text-white bg-gradient-to-r from-cyan-600 from-10% via-sky-800 via-30% to-blue-800 to-90% hover:bg-gradient-to-r hover:from-cyan-600 hover:from-10% hover:via-sky-900 hover:via-30% hover:to-blue-900 to-90 focus:outline-none">
          <div className='flex justify-center items-center'>
            <div className='ml-3'>{t('orderAgain')}</div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default SuccessfulOrder;
